/*
 * Copyright © 2022 EPO: European Patent Office.
 * ALL RIGHTS RESERVED
 */

import { useEffect, useState, useCallback, createContext, useRef } from 'react';
import { useLocation } from 'react-router-dom';
import useMediaQuery from '@material-ui/core/useMediaQuery';
import { useTheme } from '@material-ui/core';

export default function useSidebar() {
  const theme = useTheme();
  const [selectedItem, setSelectedItem] = useState('');
  const [isExpandedDrawer, setIsExpandedDrawer] = useState(false);
  const [buttonExpanded, setButtonExpanded] = useState('');
  const isMediumOrBigger = useMediaQuery(theme.breakpoints.up('lg'));
  const location = useLocation() || {};
  const navRef = useRef(null);
  const [drawerSource, setDrawerSource] = useState(null);

  useEffect(() => {
    setSelectedItem(location.pathname);
  }, [location]);

  useEffect(() => {
    if (!isExpandedDrawer) {
      setButtonExpanded('');
    }
  }, [isExpandedDrawer]);

  const onMenuButtonClick = () => {
    if (isExpandedDrawer) {
      setIsExpandedDrawer(false);
      setDrawerSource(null);
      return;
    }
    setDrawerSource('button');
    setIsExpandedDrawer(true);
  };
  const onMouseLeave = () => {
    if (drawerSource === 'navbar') {
      setIsExpandedDrawer(false);
      setDrawerSource(null);
    }
  };

  const handleItemClick = useCallback(
    (item, isItemSubList) => () => {
      if (!isItemSubList) {
        setIsExpandedDrawer(false);
      }
      if (isItemSubList) {
        setDrawerSource('navbar');
        setIsExpandedDrawer(true);
      }
      if (!isMediumOrBigger && item.link) {
        setIsExpandedDrawer(prevState => !prevState);
      }
      if (buttonExpanded === item.text) {
        setButtonExpanded('');
      } else {
        setButtonExpanded(item.text);
      }
    },
    [isMediumOrBigger, buttonExpanded],
  );

  const isSelected = useCallback(
    (item) => {
      if (item.link === selectedItem) {
        return true;
      }
      if (item.subList) {
        return item.subList.some(subListItem => subListItem.link === selectedItem);
      }
      return false;
    },
    [selectedItem],
  );

  return {
    selectedItem,
    onMouseLeave,
    onMenuButtonClick,
    setSelectedItem,
    isSelected,
    isExpandedDrawer,
    handleItemClick,
    buttonExpanded,
    setButtonExpanded,
    isMediumOrBigger,
    navRef,
  };
}

export const SidebarContext = createContext({});
